// 챗봇(sv-chat) 정형 시나리오 API. 두 진입점(StudioChatbotPage=회원 / SplashContactPage=비회원)이
// 공유한다. visibility는 서버가 FlowGate 헤더(X-User-Id 유무)로 결정하므로 FE는 굳이 구분해
// 보내지 않는다 — 로그인 상태면 axios 인터셉터가 Authorization을 붙이고, 비회원은 서버가 발급하는
// sessionId 하나로 식별이 충분하다(커스텀 헤더 없음 — FlowGate CORS 허용목록 확장 불필요).
import { apiClient, API_PRX } from '@indipia/shared';

export interface ChatFollowup {
  nodeCode: string;
  quickReply: string | null;
}

export interface ChatWelcome {
  nodeCode: string | null;
  answer: string;
  disclaimer: string;
}

export interface ChatSessionStart {
  sessionId: string;
  welcome: ChatWelcome;
  roots: ChatFollowup[];
}

export type ChatReplyType = 'STRUCTURED' | 'REPROMPT';

export interface ChatMessageResult {
  replyType: ChatReplyType;
  answer: string | null;
  guideUrl: string | null;
  prompt: string | null;
  followups: ChatFollowup[] | null;
  candidates: ChatFollowup[] | null;
}

export type ChatMessageInput =
  | { type: 'QUICK_REPLY'; nodeCode: string }
  | { type: 'FREE_TEXT'; text: string };

const HANDOFF_PREFIX = '@HANDOFF:';
// CONTACT — 핸드오프 전용 루트노드(고객상담센터_구현정의서 §CONTACT, "동작: 핸드오프"로 명시).
// 일반 시나리오 노드처럼 /messages를 거쳐 안내문구 → 후속 @HANDOFF 칩을 또 눌러야 하는 2단계 UX를
// 막기 위해, 이 노드 클릭도 @HANDOFF:* 토큰과 동일하게 즉시 핸드오프로 취급한다.
const CONTACT_NODE_CODE = 'SCN_CONTACT';

/** 후속 토큰이 상담원 연결 칩인지 판별 — FE는 이 경우 자체 핸드오프 시뮬레이션으로 전환한다. */
export function isHandoffFollowup(nodeCode: string): boolean {
  return nodeCode.startsWith(HANDOFF_PREFIX) || nodeCode === CONTACT_NODE_CODE;
}

export async function startChatSession(): Promise<ChatSessionStart> {
  const res = await apiClient.post(`${API_PRX.CHAT}/sessions`, null);
  return res.data.data as ChatSessionStart;
}

// RAG 경로는 classify→rewrite→advisor→synthesize가 순차 연쇄되는 sLLM 호출이라 공용
// apiClient 기본 타임아웃(10초, client.ts)로는 부족하다 — C-03 §8.4 LLM 호출 30초 기준에 맞춘다.
const CHAT_MESSAGE_TIMEOUT_MS = 30000;

export async function sendChatMessage(sessionId: string, input: ChatMessageInput): Promise<ChatMessageResult> {
  const res = await apiClient.post(
    `${API_PRX.CHAT}/messages`,
    { sessionId, input },
    { timeout: CHAT_MESSAGE_TIMEOUT_MS },
  );
  return res.data.data as ChatMessageResult;
}

export interface ChatHandoffContact {
  name?: string;
  phone?: string;
  email?: string;
  inquiryType?: string;
  content?: string;
  privacyConsent?: boolean;
  /** 허니팟(숨김) — 사람은 항상 비움. 값이 있으면 봇 자동제출로 간주해 서버가 조용히 거부한다(FR-10). */
  honeypot?: string;
}

export interface ChatCaseHint {
  channel: string;
  caseId: string;
  nextStep: 'REALTIME' | 'INQUIRY_FORM';
}

/** 상담원 연결(이관 경계) — 회원=실시간 안내, 비회원=접수 안내. indiChat이 sv-support에 실제로
 * case를 생성한다. memberName은 FlowGate 주입 헤더에 이름이 없어 indiChat이 조회할 방법이 없으므로,
 * 이미 로그인 세션에 있는 값을 회원 쪽(StudioChatbotPage)에서만 실어 보낸다. */
export async function sendChatHandoff(
  sessionId: string,
  opts?: { route?: string; contact?: ChatHandoffContact; memberName?: string },
): Promise<ChatCaseHint> {
  const res = await apiClient.post(`${API_PRX.CHAT}/handoff`, {
    sessionId,
    route: opts?.route,
    contact: opts?.contact,
    memberName: opts?.memberName,
  });
  return res.data.data.caseHint as ChatCaseHint;
}
